import { useState, useEffect } from 'react';
import {
  AdminDashboardMetrics,
  AdminUserGrowthData,
  AdminSubscriptionDistribution,
  AdminActivityFeedItem,
} from '../../types/admin';

/**
 * useAdminDashboard - Hook for dashboard data and operations
 *
 * Provides:
 * - Dashboard metrics (users, homes, MRR, businesses)
 * - User growth data (30 days)
 * - Subscription distribution
 * - Activity feed (recent 25 activities)
 *
 * Currently uses mock data for development.
 * TODO: Implement API calls to /api/admin/dashboard/*
 * TODO: Add auto-refresh functionality
 * TODO: Add caching
 */
export function useAdminDashboard() {
  const [metrics, setMetrics] = useState<AdminDashboardMetrics | null>(null);
  const [userGrowth, setUserGrowth] = useState<AdminUserGrowthData[]>([]);
  const [subscriptionDist, setSubscriptionDist] = useState<AdminSubscriptionDistribution[]>([]);
  const [activityFeed, setActivityFeed] = useState<AdminActivityFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate realistic mock data
  const generateMockData = () => {
    // Dashboard Metrics
    const mockMetrics: AdminDashboardMetrics = {
      totalUsers: 12847,
      totalUsersChange: 8.3,
      activeHomes: 8932,
      activeHomesChange: 12.5,
      mrr: 48650,
      mrrChange: 15.7,
      activeBusinesses: 342,
      activeBusinessesChange: 5.2,
    };

    // User Growth Data (last 30 days)
    const mockUserGrowth: AdminUserGrowthData[] = [];
    const today = new Date();
    let totalUsers = 12450; // Starting number 30 days ago

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      // Simulate varying growth with some randomness and an upward trend
      const baseGrowth = 10 + Math.floor(Math.random() * 15);
      const weekendFactor = date.getDay() === 0 || date.getDay() === 6 ? 0.6 : 1;
      const newUsers = Math.floor(baseGrowth * weekendFactor);

      totalUsers += newUsers;

      mockUserGrowth.push({
        date: date.toISOString().split('T')[0],
        users: totalUsers,
        newUsers: newUsers,
      });
    }

    // Subscription Distribution
    const mockSubscriptionDist: AdminSubscriptionDistribution[] = [
      { tier: 'Free', count: 9478, percentage: 73.8 },
      { tier: 'Pro', count: 2541, percentage: 19.8 },
      { tier: 'Agent', count: 628, percentage: 4.9 },
      { tier: 'Business', count: 200, percentage: 1.5 },
    ];

    // Activity Feed - Generate realistic recent activities
    const mockActivityFeed: AdminActivityFeedItem[] = [
      {
        id: '1',
        type: 'user_signup',
        description: 'Signed up via Google OAuth',
        user: {
          name: 'Sarah Mitchell',
          email: 'sarah.mitchell@gmail.com',
        },
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      },
      {
        id: '2',
        type: 'subscription_change',
        description: 'Upgraded to Pro plan',
        user: {
          name: 'James Rodriguez',
          email: 'james.r@outlook.com',
        },
        timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
      },
      {
        id: '3',
        type: 'home_created',
        description: 'Created new home "Modern Downtown Condo"',
        user: {
          name: 'Emily Chen',
          email: 'emily.chen@yahoo.com',
        },
        timestamp: new Date(Date.now() - 23 * 60 * 1000).toISOString(),
      },
      {
        id: '4',
        type: 'business_created',
        description: 'Registered "Premium Realty Group"',
        user: {
          name: 'Michael Thompson',
          email: 'michael@premiumrealty.com',
        },
        timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(),
      },
      {
        id: '5',
        type: 'user_signup',
        description: 'Signed up via email',
        user: {
          name: 'David Park',
          email: 'david.park@gmail.com',
        },
        timestamp: new Date(Date.now() - 48 * 60 * 1000).toISOString(),
      },
      {
        id: '6',
        type: 'home_created',
        description: 'Created new home "Suburban Family Home"',
        user: {
          name: 'Lisa Anderson',
          email: 'lisa.anderson@gmail.com',
        },
        timestamp: new Date(Date.now() - 67 * 60 * 1000).toISOString(),
      },
      {
        id: '7',
        type: 'subscription_change',
        description: 'Upgraded to Agent plan',
        user: {
          name: 'Robert Martinez',
          email: 'rmartinez@realtyexperts.com',
        },
        timestamp: new Date(Date.now() - 82 * 60 * 1000).toISOString(),
      },
      {
        id: '8',
        type: 'user_signup',
        description: 'Signed up via Google OAuth',
        user: {
          name: 'Amanda Wilson',
          email: 'amanda.wilson@gmail.com',
        },
        timestamp: new Date(Date.now() - 95 * 60 * 1000).toISOString(),
      },
      {
        id: '9',
        type: 'home_created',
        description: 'Created new home "Lakefront Property"',
        user: {
          name: 'Kevin Brown',
          email: 'kevin.brown@yahoo.com',
        },
        timestamp: new Date(Date.now() - 110 * 60 * 1000).toISOString(),
      },
      {
        id: '10',
        type: 'subscription_change',
        description: 'Upgraded to Pro plan',
        user: {
          name: 'Jennifer Lee',
          email: 'jennifer.lee@gmail.com',
        },
        timestamp: new Date(Date.now() - 134 * 60 * 1000).toISOString(),
      },
      {
        id: '11',
        type: 'business_created',
        description: 'Registered "Coastal Homes Realty"',
        user: {
          name: 'Christopher Davis',
          email: 'chris@coastalhomes.com',
        },
        timestamp: new Date(Date.now() - 156 * 60 * 1000).toISOString(),
      },
      {
        id: '12',
        type: 'user_signup',
        description: 'Signed up via email',
        user: {
          name: 'Michelle Taylor',
          email: 'michelle.t@outlook.com',
        },
        timestamp: new Date(Date.now() - 178 * 60 * 1000).toISOString(),
      },
      {
        id: '13',
        type: 'home_created',
        description: 'Created new home "Mountain View Retreat"',
        user: {
          name: 'Daniel Garcia',
          email: 'daniel.garcia@gmail.com',
        },
        timestamp: new Date(Date.now() - 203 * 60 * 1000).toISOString(),
      },
      {
        id: '14',
        type: 'subscription_change',
        description: 'Upgraded to Agent plan',
        user: {
          name: 'Nicole Johnson',
          email: 'nicole@eliterealty.com',
        },
        timestamp: new Date(Date.now() - 227 * 60 * 1000).toISOString(),
      },
      {
        id: '15',
        type: 'user_signup',
        description: 'Signed up via Google OAuth',
        user: {
          name: 'William Smith',
          email: 'william.smith@gmail.com',
        },
        timestamp: new Date(Date.now() - 251 * 60 * 1000).toISOString(),
      },
      {
        id: '16',
        type: 'home_created',
        description: 'Created new home "Urban Loft"',
        user: {
          name: 'Rachel Green',
          email: 'rachel.green@yahoo.com',
        },
        timestamp: new Date(Date.now() - 289 * 60 * 1000).toISOString(),
      },
      {
        id: '17',
        type: 'subscription_change',
        description: 'Upgraded to Pro plan',
        user: {
          name: 'Thomas White',
          email: 'thomas.white@gmail.com',
        },
        timestamp: new Date(Date.now() - 315 * 60 * 1000).toISOString(),
      },
      {
        id: '18',
        type: 'user_signup',
        description: 'Signed up via email',
        user: {
          name: 'Sophia Martinez',
          email: 'sophia.m@outlook.com',
        },
        timestamp: new Date(Date.now() - 342 * 60 * 1000).toISOString(),
      },
      {
        id: '19',
        type: 'business_created',
        description: 'Registered "Metro Properties LLC"',
        user: {
          name: 'Matthew Anderson',
          email: 'matt@metroproperties.com',
        },
        timestamp: new Date(Date.now() - 378 * 60 * 1000).toISOString(),
      },
      {
        id: '20',
        type: 'home_created',
        description: 'Created new home "Beachside Villa"',
        user: {
          name: 'Olivia Harris',
          email: 'olivia.harris@gmail.com',
        },
        timestamp: new Date(Date.now() - 412 * 60 * 1000).toISOString(),
      },
      {
        id: '21',
        type: 'subscription_change',
        description: 'Upgraded to Agent plan',
        user: {
          name: 'Andrew Clark',
          email: 'andrew@clarkrealty.com',
        },
        timestamp: new Date(Date.now() - 456 * 60 * 1000).toISOString(),
      },
      {
        id: '22',
        type: 'user_signup',
        description: 'Signed up via Google OAuth',
        user: {
          name: 'Isabella Lopez',
          email: 'isabella.lopez@gmail.com',
        },
        timestamp: new Date(Date.now() - 489 * 60 * 1000).toISOString(),
      },
      {
        id: '23',
        type: 'home_created',
        description: 'Created new home "Countryside Estate"',
        user: {
          name: 'Ethan Walker',
          email: 'ethan.walker@yahoo.com',
        },
        timestamp: new Date(Date.now() - 523 * 60 * 1000).toISOString(),
      },
      {
        id: '24',
        type: 'subscription_change',
        description: 'Upgraded to Pro plan',
        user: {
          name: 'Mia Robinson',
          email: 'mia.robinson@gmail.com',
        },
        timestamp: new Date(Date.now() - 567 * 60 * 1000).toISOString(),
      },
      {
        id: '25',
        type: 'user_signup',
        description: 'Signed up via email',
        user: {
          name: 'Alexander King',
          email: 'alex.king@outlook.com',
        },
        timestamp: new Date(Date.now() - 601 * 60 * 1000).toISOString(),
      },
    ];

    return {
      mockMetrics,
      mockUserGrowth,
      mockSubscriptionDist,
      mockActivityFeed,
    };
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // TODO: Replace with actual API calls
      // const response = await fetch('/api/admin/dashboard', {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //   },
      // });
      // const data = await response.json();

      // For now, use mock data
      const {
        mockMetrics,
        mockUserGrowth,
        mockSubscriptionDist,
        mockActivityFeed,
      } = generateMockData();

      setMetrics(mockMetrics);
      setUserGrowth(mockUserGrowth);
      setSubscriptionDist(mockSubscriptionDist);
      setActivityFeed(mockActivityFeed);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return {
    metrics,
    userGrowth,
    subscriptionDist,
    activityFeed,
    isLoading,
    error,
    refreshDashboard: fetchDashboardData,
  };
}
